import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Chip, Paper, Typography } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import dayjs from 'dayjs';
import WarningPage from './WarningPage';
import WarningLoading from './WarningLoading';
import RetryNotice from './RetryNotice';
import WarningMetricTable from './WarningMetricTable';
import MetricDefinitionNote from './MetricDefinitionNote';
import CountLink from './CountLink';
import ProjectDuesTasksDialog from './ProjectDuesTasksDialog';
import { departmentLabel } from './departments';
import { useDashboardRequest } from './useDashboardRequest';
import { fmtDay } from './drilldownCells';
import WorkedTimer from './WorkedTimer';
import useWorkedToday from './useWorkedToday';
import { workedCsv } from './workedTime';
import { PROJECT_DUES_URL, percentText, prefetchProjectDuesTasks, projectLateText, requestDates, requestSubject } from './projectDues';
import { SEVERITY, WD } from './warningTheme';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

const days = (n) => (n === null || n === undefined ? '-' : `${n} ${n === 1 ? 'day' : 'days'}`);

// One headline number. Plain text when there is nothing behind it; otherwise it opens the tasks it is made of.
const Tile = ({ label, value, hint, bad, onOpen, onPrefetch, listLabel, testId }) => (
  <Paper variant="outlined" data-testid={testId} sx={{ p: 2, borderRadius: 3, borderColor: bad && value > 0 ? SEVERITY.warning.accent : WD.border, bgcolor: bad && value > 0 ? SEVERITY.warning.pillBg : '#fff' }}>
    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: WD.inkMuted }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: 34, fontWeight: 700, lineHeight: 1.15, mt: 0.5, color: bad && value > 0 ? SEVERITY.warning.pillText : WD.ink, fontVariantNumeric: 'tabular-nums' }}>
      {onOpen ? <CountLink count={value} label={listLabel} onOpen={onOpen} onPrefetch={onPrefetch} /> : value}
    </Typography>
    {hint && <Typography sx={{ fontSize: 12, color: WD.inkMuted, mt: 0.25 }}>{hint}</Typography>}
  </Paper>
);

const Section = ({ title, desc, children }) => (
  <Box sx={{ mt: 4 }}>
    <Typography component="h2" sx={{ fontSize: 20, fontWeight: 700, color: WD.ink }}>{title}</Typography>
    {desc && <Typography sx={{ fontSize: 13, color: WD.inkMuted, mt: 0.25, mb: 1.5 }}>{desc}</Typography>}
    {children}
  </Box>
);

const ProjectDuesDashboard = () => {
  const [reloadKey, setReloadKey] = useState(0);
  // freshness 10s: it is live work, so a short stay away still refreshes it quietly (same as the home section, so they share the answer)
  const { data: answer, error, loading } = useDashboardRequest(PROJECT_DUES_URL, reloadKey, { freshMs: 10 * 1000 });
  // an answer in a shape this page cannot read is "could not load", never a crash
  const data = answer && answer.totals && Array.isArray(answer.projects) && Array.isArray(answer.people) && Array.isArray(answer.dateRequests) ? answer : null;
  const unreadable = !!answer && !data;
  const [open, setOpen] = useState(null); // the task list popup: { kind, projectId?, userId?, projectName?, userName? }

  const totals = data?.totals;
  const asOf = data?.asOf ? fmtDay(data.asOf) : '';
  const nothingYet = !!data && totals.totalTasks === 0 && data.projects.length === 0;

  // Worked time today (the Attendance page's timer): yours at the top, and each person's in the table - asked once the people are known,
  // and kept fresh while the page is open. Which people the viewer may see is decided by the server (own, reporting line, HR).
  const workedIds = data ? [...new Set(data.people.map((p) => p.entityId))] : null;
  const worked = useWorkedToday(workedIds, reloadKey);
  const workedOf = (id) => (worked.data ? worked.data.people[id] : undefined);

  const list = (request) => ({ onOpen: () => setOpen(request), onPrefetch: () => prefetchProjectDuesTasks(request) });

  // most worrying people first: most overdue, then most open, then by name (the table keeps this order)
  const people = useMemo(
    () => [...(data?.people || [])].sort((a, b) => b.overdueTasks - a.overdueTasks || b.openTasks - a.openTasks || String(a.entityName).localeCompare(String(b.entityName))),
    [data],
  );

  const projectColumns = [
    {
      field: 'name', headerName: 'Project',
      render: (p) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
          <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ mt: 0.25 }}>
            {p.isOverdue && <Chip size="small" color="error" variant="outlined" label={`Project ${projectLateText(p)}`} />}
            {p.isDone && <Chip size="small" color="success" variant="outlined" label="Completed" />}
          </Box>
        </Box>
      ),
      csvValue: (p) => p.name,
    },
    { field: 'dueDate', headerName: 'Due date', render: (p) => (p.dueDate ? fmtDay(p.dueDate) : '-'), csvValue: (p) => p.dueDate || '' },
    { field: 'openTasks', headerName: 'Open', align: 'right', render: (p) => <CountLink count={p.openTasks} label={`open tasks in ${p.name}`} {...list({ kind: 'open', projectId: p.projectId, projectName: p.name })} />, csvValue: (p) => p.openTasks },
    { field: 'overdueTasks', headerName: 'Overdue', align: 'right', render: (p) => <CountLink count={p.overdueTasks} label={`overdue tasks in ${p.name}`} {...list({ kind: 'overdue', projectId: p.projectId, projectName: p.name })} />, csvValue: (p) => p.overdueTasks },
    { field: 'dueSoonTasks', headerName: 'Due in 7 days', align: 'right', render: (p) => <CountLink count={p.dueSoonTasks} label={`tasks due soon in ${p.name}`} {...list({ kind: 'due_soon', projectId: p.projectId, projectName: p.name })} />, csvValue: (p) => p.dueSoonTasks },
    { field: 'oldestOverdueDays', headerName: 'Longest overdue', align: 'right', render: (p) => days(p.oldestOverdueDays), csvValue: (p) => (p.oldestOverdueDays ?? '') },
    { field: 'donePercent', headerName: 'Done', align: 'right', render: (p) => `${percentText(p.donePercent)}${p.totalTasks ? ` (${p.doneTasks}/${p.totalTasks})` : ''}`, csvValue: (p) => (p.donePercent ?? '') },
  ];

  const personColumns = [
    {
      field: 'entityName', headerName: 'Person',
      render: (p) => (
        <Typography variant="body2" fontStyle={p.isActive === false ? 'italic' : 'normal'} color={p.isActive === false ? 'error.main' : 'text.primary'}>
          {p.entityName}{p.isActive === false && ' (Inactive)'}
        </Typography>
      ),
      csvValue: (p) => `${p.entityName}${p.isActive === false ? ' (Inactive)' : ''}`,
    },
    { field: 'departmentId', headerName: 'Department', render: (p) => departmentName(p.departmentId), csvValue: (p) => departmentName(p.departmentId) },
    {
      field: 'worked', headerName: 'Worked today', align: 'right',
      render: (p) => <WorkedTimer worked={workedOf(p.entityId)} loading={worked.loading} />,
      csvValue: (p) => workedCsv(workedOf(p.entityId)),
    },
    { field: 'openTasks', headerName: 'Open', align: 'right', render: (p) => <CountLink count={p.openTasks} label={`open tasks of ${p.entityName}`} {...list({ kind: 'open', userId: p.entityId, userName: p.entityName })} />, csvValue: (p) => p.openTasks },
    { field: 'overdueTasks', headerName: 'Overdue', align: 'right', render: (p) => <CountLink count={p.overdueTasks} label={`overdue tasks of ${p.entityName}`} {...list({ kind: 'overdue', userId: p.entityId, userName: p.entityName })} />, csvValue: (p) => p.overdueTasks },
    { field: 'dueSoonTasks', headerName: 'Due in 7 days', align: 'right', render: (p) => <CountLink count={p.dueSoonTasks} label={`tasks due soon of ${p.entityName}`} {...list({ kind: 'due_soon', userId: p.entityId, userName: p.entityName })} />, csvValue: (p) => p.dueSoonTasks },
    { field: 'oldestOverdueDays', headerName: 'Longest overdue', align: 'right', render: (p) => days(p.oldestOverdueDays), csvValue: (p) => (p.oldestOverdueDays ?? '') },
    { field: 'donePercent', headerName: 'Done', align: 'right', render: (p) => `${percentText(p.donePercent)} (${p.doneTasks}/${p.doneTasks + p.openTasks})`, csvValue: (p) => (p.donePercent ?? '') },
  ];

  const requestColumns = [
    {
      field: 'subject', headerName: 'Request',
      render: (r) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{requestSubject(r)}</Typography>
          <Typography variant="caption" color="text.secondary">{r.kind === 'task' ? `Task in ${r.projectName || 'a project'}` : 'The project itself'}</Typography>
        </Box>
      ),
      csvValue: (r) => `${r.kind === 'task' ? 'Task' : 'Project'}: ${requestSubject(r)}`,
    },
    { field: 'assignees', headerName: 'Assigned to', render: (r) => ((r.assignees || []).map((a) => a.name).join(', ') || '-'), csvValue: (r) => (r.assignees || []).map((a) => a.name).join(', ') },
    { field: 'dates', headerName: 'Due date change', render: (r) => requestDates(r), csvValue: (r) => requestDates(r) },
    { field: 'reason', headerName: 'Reason', render: (r) => r.reason || '-', csvValue: (r) => r.reason || '' },
    { field: 'waitingDays', headerName: 'Waiting', align: 'right', render: (r) => days(r.waitingDays), csvValue: (r) => (r.waitingDays ?? '') },
  ];

  return (
    <WarningPage
      title="Project Dues"
      actions={(
        <Box display="flex" gap={1}>
          <Button component={Link} to="/dashboard/warning-dashboard" variant="text">Back to Warning Dashboard</Button>
          <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={() => setReloadKey((k) => k + 1)}>Refresh</Button>
        </Box>
      )}
    >
      <Box sx={{ mb: 2 }}>
        <MetricDefinitionNote metricKey="project_dues" />
      </Box>

      {loading && !answer && <WarningLoading message="Loading project dues…" />}
      {!loading && !data && (error || unreadable) && <RetryNotice message={error || 'Project dues came back in a form this page cannot read'} onRetry={() => setReloadKey((k) => k + 1)} />}

      {data && (
        <>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 13, color: WD.inkMuted }}>
              InteTasks · as of {asOf || dayjs().format('DD MMM YYYY')} · your work and your team's · numbers only, no warnings
            </Typography>
            <Paper variant="outlined" data-testid="my-worked-time" sx={{ px: 2, py: 1, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: WD.inkMuted }}>Your time today</Typography>
              {worked.error && !worked.data
                ? <Typography sx={{ fontSize: '0.85rem', color: WD.inkMuted }}>Could not load</Typography>
                : <WorkedTimer worked={worked.data?.me} loading={worked.loading} large />}
            </Paper>
          </Box>

          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' } }}>
            <Tile testId="tile-overdue" label="Overdue tasks" value={totals.overdueTasks} bad hint="Open and past the due date" listLabel="overdue tasks" {...list({ kind: 'overdue' })} />
            <Tile testId="tile-today" label="Due today" value={totals.dueToday} hint="Not yet past their time" listLabel="tasks due today" {...list({ kind: 'due_today' })} />
            <Tile testId="tile-soon" label="Due in 7 days" value={totals.dueSoonTasks} hint="Today included" listLabel="tasks due in the next 7 days" {...list({ kind: 'due_soon' })} />
            <Tile testId="tile-projects" label="Overdue projects" value={totals.overdueProjects} bad hint={`of ${totals.openProjects} open`} />
            <Tile testId="tile-requests" label="Date requests waiting" value={totals.pendingDateRequests} hint="No decision yet" />
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" sx={{ mt: 1.5 }}>
            <Chip size="small" label={`Overdue ≤ 7 days: ${totals.ageing.upTo7}`} />
            <Chip size="small" label={`8–30 days: ${totals.ageing.from8To30}`} />
            <Chip size="small" label={`Over 30 days: ${totals.ageing.over30}`} />
            <Chip size="small" variant="outlined" label={`Longest overdue: ${days(totals.oldestOverdueDays)}`} />
            <Box sx={{ flex: 1 }} />
            <Typography component="div" sx={{ fontSize: 13, color: WD.inkMuted, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <span>Open <CountLink count={totals.openTasks} label="open tasks" {...list({ kind: 'open' })} /></span>
              <span>Completed <CountLink count={totals.doneTasks} label="completed tasks" {...list({ kind: 'done' })} /></span>
              <span>No due date <CountLink count={totals.tasksWithoutDueDate} label="tasks with no due date" {...list({ kind: 'no_due_date' })} /></span>
            </Typography>
          </Box>

          {nothingYet ? (
            <Box sx={{ textAlign: 'center', py: 5, px: 2, mt: 4, bgcolor: '#fff', border: `1px dashed ${WD.border}`, borderRadius: '16px' }}>
              <Typography sx={{ fontSize: 20, fontWeight: 600 }}>No InteTasks work is assigned to you or your team yet</Typography>
              <Typography sx={{ fontSize: 13, color: WD.inkMuted, mt: 0.5 }}>As soon as a task is assigned to you or to someone under you in InteTasks, its dues appear here.</Typography>
            </Box>
          ) : (
            <>
              <Section title="By project" desc="Projects where you or your team have work - late projects first, then the ones with the most overdue tasks. Click a number to see the tasks.">
                <WarningMetricTable rows={data.projects} columns={projectColumns} searchFields={['name']} emptyMessage="No projects." exportFileName="Project_Dues_By_Project" rowKey={(p) => p.projectId} />
              </Section>

              <Section title="By person" desc="You and the people under you who have tasks, most overdue first. A task with several people counts for each of them.">
                <WarningMetricTable rows={people} columns={personColumns} searchFields={['entityName']} departmentName={departmentName} emptyMessage="No tasks are assigned to you or your team yet." exportFileName="Project_Dues_By_Person" rowKey={(p) => p.entityId} />
              </Section>

              <Section title="Date-change requests waiting" desc="Someone asked to move a due date and nobody has decided yet. Longest waiting first.">
                <WarningMetricTable rows={data.dateRequests} columns={requestColumns} searchFields={['taskTitle', 'projectName', 'reason']} emptyMessage="No date-change requests are waiting." exportFileName="Project_Dues_Date_Requests" rowKey={(r) => r.requestId} />
              </Section>
            </>
          )}
        </>
      )}

      {open && <ProjectDuesTasksDialog request={open} onClose={() => setOpen(null)} />}
    </WarningPage>
  );
};

export default ProjectDuesDashboard;
